import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import BuyerPortalLayout from '../../components/buyer/BuyerPortalLayout';
import { fetchBuyerRfqDetail, publishBuyerRfq, fetchQuotationsForRfq } from '../../lib/buyerApi';

const BuyerRfqDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [rfq, setRfq] = useState<any>(null);
  const [items, setItems] = useState<any[]>([]);
  const [quotations, setQuotations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [rfqRes, quotesRes] = await Promise.allSettled([
        fetchBuyerRfqDetail(parseInt(id)),
        fetchQuotationsForRfq(parseInt(id)),
      ]);
      if (rfqRes.status === 'fulfilled') {
        setRfq(rfqRes.value.rfq || rfqRes.value);
        setItems(rfqRes.value.items || []);
      }
      if (quotesRes.status === 'fulfilled') {
        setQuotations(quotesRes.value.quotations || quotesRes.value.items || []);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handlePublish = async () => {
    if (!id) return;
    try { await publishBuyerRfq(parseInt(id)); load(); }
    catch (err: any) { alert(err.message || 'Failed to publish'); }
  };

  if (loading) return <BuyerPortalLayout title="RFQ Detail"><div className="flex items-center justify-center py-16"><div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div></BuyerPortalLayout>;
  if (!rfq) return <BuyerPortalLayout title="RFQ Not Found"><div className="text-center py-16"><p className="text-gray-400">RFQ not found</p><Link to="/account/rfqs" className="mt-3 text-sm text-emerald-600 font-bold hover:underline">← Back</Link></div></BuyerPortalLayout>;

  return (
    <BuyerPortalLayout title={rfq.title || rfq.rfq_number}>
      <Link to="/account/rfqs" className="text-sm text-gray-500 hover:text-gray-700 mb-4 inline-block">← Back to RFQs</Link>

      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-extrabold text-gray-900">{rfq.title}</h2>
            <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
              rfq.status === 'awarded' ? 'bg-emerald-100 text-emerald-700' :
              rfq.status === 'published' ? 'bg-blue-100 text-blue-700' :
              rfq.status === 'draft' ? 'bg-gray-100 text-gray-600' : 'bg-amber-100 text-amber-700'
            }`}>{rfq.status}</span>
          </div>
          <p className="text-sm text-gray-500 mt-1">{rfq.rfq_number} · {(rfq.visibility || '').replace(/_/g, ' ')}</p>
        </div>
        {rfq.status === 'draft' && (
          <button onClick={handlePublish} className="px-5 py-2.5 bg-emerald-600 text-white text-sm font-bold rounded-xl hover:bg-emerald-700 transition">Publish RFQ</button>
        )}
      </div>

      {rfq.description && (
        <div className="bg-white border border-gray-100 rounded-2xl p-5 mb-4">
          <p className="text-sm text-gray-600 whitespace-pre-wrap">{rfq.description}</p>
        </div>
      )}

      {/* Items */}
      <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden mb-4">
        <div className="px-5 py-3 border-b border-gray-100"><h3 className="text-sm font-bold text-gray-700">Requested Items ({items.length})</h3></div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50"><tr>
            <th className="px-5 py-2.5 text-left font-bold text-gray-500 text-xs uppercase">Product</th>
            <th className="px-5 py-2.5 text-left font-bold text-gray-500 text-xs uppercase">Quantity</th>
            <th className="px-5 py-2.5 text-left font-bold text-gray-500 text-xs uppercase">Grade</th>
            <th className="px-5 py-2.5 text-right font-bold text-gray-500 text-xs uppercase">Max Price</th>
          </tr></thead>
          <tbody className="divide-y divide-gray-50">
            {items.map((item: any) => (
              <tr key={item.id}>
                <td className="px-5 py-3 font-medium text-gray-900">{item.title}</td>
                <td className="px-5 py-3 text-gray-600">{item.commercial_quantity} {item.commercial_unit}</td>
                <td className="px-5 py-3 text-gray-500">{item.grade || '—'}</td>
                <td className="px-5 py-3 text-right">{item.max_price ? `${rfq.currency} ${Number(item.max_price).toLocaleString()}` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Quotations */}
      <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100"><h3 className="text-sm font-bold text-gray-700">Supplier Quotations ({quotations.length})</h3></div>
        {quotations.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-gray-400">No quotations yet</div>
        ) : (
          <div className="divide-y divide-gray-50">
            {quotations.map((q: any) => (
              <div key={q.id} className="px-5 py-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-bold text-gray-900">{q.supplier_name || `Supplier #${q.supplier_tenant_id}`}</p>
                    <p className="text-xs text-gray-400">{q.quotation_number}</p>
                    <span className={`mt-1 inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${
                      q.status === 'accepted' ? 'bg-emerald-100 text-emerald-700' :
                      q.status === 'submitted' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>{q.status}</span>
                  </div>
                  <p className="text-lg font-extrabold text-gray-900">{q.currency} {Number(q.total_amount || 0).toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </BuyerPortalLayout>
  );
};

export default BuyerRfqDetailPage;
